/**
 * iOS Detection Mixin
 * Provides methods to detect iOS Safari, standalone mode (PWA), and device type
 * Used for showing iOS Home Screen prompts for push notification support
 */
export default {
  methods: {
    /**
     * Detects if the user is on iOS Safari (not Chrome, Firefox, or Edge on iOS)
     * @returns {boolean}
     */
    isIOSSafari() {
      const ua = navigator.userAgent;
      
      // Check for iOS device
      const isIOS = /iPad|iPhone|iPod/.test(ua) || 
                    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      
      // Check for Safari (exclude Chrome, Firefox, Edge on iOS)
      const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
      
      return isIOS && isSafari;
    },

    /**
     * Gets the iOS version number
     * @returns {number|null} The iOS version (e.g., 16.4) or null if not iOS
     */
    getIOSVersion() {
      const ua = navigator.userAgent;
      
      // Match iOS version from user agent
      // Format: "OS 16_4" or "OS 16_4_1"
      const match = ua.match(/OS (\d+)_(\d+)(?:_(\d+))?/);
      
      if (match) {
        const major = parseInt(match[1], 10);
        const minor = parseInt(match[2], 10);
        return parseFloat(`${major}.${minor}`);
      }
      
      // For iPad with desktop UA, try to detect from other hints
      if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) {
        // iPad with desktop mode - assume modern iOS (16.4+)
        // This is a fallback since desktop UA doesn't expose version
        return 16.4;
      }
      
      return null;
    },

    /**
     * Checks if iOS version supports PWA push notifications (16.4+)
     * @returns {boolean}
     */
    isIOSVersionSupported() {
      const version = this.getIOSVersion();
      return version !== null && version >= 16.4;
    },

    /**
     * Detects if the app is running in standalone mode (added to Home Screen)
     * @returns {boolean}
     */
    isStandalone() {
      // iOS Safari standalone mode
      if (window.navigator.standalone === true) {
        return true;
      }
      
      // Other browsers (Android, desktop PWA)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }
      
      return false;
    },

    /**
     * Detects if the device is an iPhone (vs iPad) based on screen width
     * Used to show correct Share button location instructions
     * @returns {boolean}
     */
    isIPhone() {
      // iPhones typically have screen width < 768px
      // iPads have larger screens
      return window.innerWidth < 768 && this.isIOSSafari();
    },

    /**
     * Gets the location text for the Share button based on device
     * @returns {string} "bottom" for iPhone, "top-right" for iPad
     */
    getShareButtonLocation() {
      return this.isIPhone() ? 'bottom' : 'top-right';
    },

    /**
     * Checks if the iOS Home Screen prompt has been dismissed
     * @returns {boolean}
     */
    isIOSPromptDismissed() {
      return localStorage.getItem('the_w_iosHomeScreenPromptDismissed') === 'true';
    },

    /**
     * Dismisses the iOS Home Screen prompt permanently
     */
    dismissIOSPrompt() {
      localStorage.setItem('the_w_iosHomeScreenPromptDismissed', 'true');
    },

    /**
     * Re-enables the iOS Home Screen prompt (for settings)
     */
    resetIOSPromptDismissal() {
      localStorage.removeItem('the_w_iosHomeScreenPromptDismissed');
    },

    /**
     * Determines if the iOS Home Screen prompt should be shown
     * Only shows for iOS 16.4+ Safari users not in standalone mode
     * @returns {boolean}
     */
    shouldShowIOSHomeScreenPrompt() {
      return this.isIOSSafari() && 
             this.isIOSVersionSupported() && 
             !this.isStandalone() && 
             !this.isIOSPromptDismissed();
    },

    /**
     * Checks if this is an older iOS device that doesn't support PWA push
     * @returns {boolean}
     */
    isOldIOSDevice() {
      return this.isIOSSafari() && !this.isIOSVersionSupported();
    }
  }
};
